import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { BrandLogo, BrandIntroduction } from "@/components/common/BrandIdentity";
import { postLoginPath } from "@/lib/authRouting";
import { MfaFlow } from "@/components/settings/MfaPanel";

export default function Login() {
  const { login, completeMfa } = useApp();
  const [challenge, setChallenge] = useState(() => JSON.parse(sessionStorage.getItem("nyk_mfa_challenge") || "null"));
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!/^\S+@\S+\.\S+$/.test(email)) errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";
    setErr(errs);
    if (Object.keys(errs).length) return;
    try {
      const result = await login(email, password);
      if (result?.mfaRequired) { setChallenge(result); return; }
      toast.success("Welcome back.");
      navigate(postLoginPath(result));
    } catch (e2) {
      const msg = e2.response?.data?.detail || "Invalid email or password";
      setErr({ password: typeof msg === "string" ? msg : "Sign in failed" });
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand panel */}
      <div className="brand-panel relative hidden w-[48%] flex-col justify-between gap-7 bg-navy p-10 text-white lg:flex xl:p-12" data-testid="login-brand-panel">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-12 w-12" testId="login-brand-logo" />
          <div>
            <div className="font-display text-2xl font-semibold tracking-[0.12em] text-gold">NYAYYAKSHA</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold/70">Advocate intelligence & drafting</div>
          </div>
        </div>
        <div className="w-full max-w-xl self-center">
          <BrandIntroduction />
          <div className="brand-copy-enter mt-9">
            <h1 className="font-display text-4xl font-semibold leading-tight text-gold-light xl:text-5xl">The legal operating system<br />for Indian advocates.</h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-navy-text/80">Every matter, hearing, draft and payment — connected, intelligent and always within reach.</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 text-[10px] tracking-wide text-navy-text/50"><span>© {new Date().getFullYear()} Adusumilli &amp; Associates</span><span>Visakhapatnam</span></div>
      </div>

      {/* Login card */}
      <div className="flex w-full flex-1 items-center justify-center bg-paper px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <BrandLogo className="h-16 w-16" testId="login-mobile-brand-logo" />
            <div className="font-display text-3xl font-semibold text-ink">NYAYYAKSHA</div>
          </div>
          <h2 className="font-display text-3xl font-semibold text-ink">Sign in</h2>
          <p className="mt-1 text-sm text-ink-2">Access your legal workspace.</p>

          {challenge ? <MfaFlow {...challenge} onDone={async (data) => { const result = await completeMfa(data); navigate(postLoginPath(result)); }} onCancel={() => { sessionStorage.removeItem("nyk_mfa_challenge"); setChallenge(null); }} /> : <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-ink-2">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className={err.email ? "border-danger" : ""} />
              {err.email && <p className="text-xs text-danger">{err.email}</p>}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-ink-2">Password</Label>
                <button type="button" disabled className="text-xs text-ink-muted disabled:cursor-not-allowed" title="Password recovery is unavailable: no email provider has been selected." data-testid="login-forgot-password">Forgot password? Unavailable</button>
              </div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className={err.password ? "border-danger" : ""} />
              {err.password && <p className="text-xs text-danger">{err.password}</p>}
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-2">
              <Checkbox defaultChecked /> Remember me
            </label>
            <Button type="submit" className="w-full" data-testid="login-submit">Sign In</Button>
            <Button type="button" variant="outline" className="w-full" onClick={googleLogin} data-testid="login-google">
              Continue with Google
            </Button>
          </form>}

          <p className="mt-6 text-center text-sm text-ink-2">
            New to Nyayyaksha? <Link to="/signup" className="font-medium text-copper hover:underline">Create account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
